import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { getApiErrorMessage } from '@/api/apiErrors';
import { setAuthTokens } from '@/auth/tokenStorage';
import { acceptInvite } from '@/api/adminAccess';
import { EyeIcon, PasswordIcon, UserIcon } from '@/assets/icons/onboarding/icons';
import { InputForm } from '@/components/CustomForm/components';
import { Button } from '@/components/common/Button';
import { LoginLayout } from '@/components/layout/LoginLayout';
import '@/pages/auth/invite-accept.scss';

type InviteAcceptValues = {
  first_name: string;
  last_name: string;
  password: string;
  password2: string;
};

export function InviteAcceptPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inviteToken = searchParams.get('token')?.trim() ?? '';
  const hasInviteToken = inviteToken.length > 0;
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<InviteAcceptValues>({
    defaultValues: {
      first_name: '',
      last_name: '',
      password: '',
      password2: '',
    },
    mode: 'onChange',
  });
  const passwordValue = watch('password');
  const confirmPasswordValue = watch('password2');

  const onSubmit = handleSubmit(async (values) => {
    if (!hasInviteToken) {
      toast.error(t('auth.inviteMissingTokenBody'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await acceptInvite({
        token: inviteToken,
        password: values.password,
        password2: values.password2,
        first_name: values.first_name || undefined,
        last_name: values.last_name || undefined,
      });

      setAuthTokens({
        access: response.access,
        refresh: response.refresh,
      });
      toast.success(t('auth.inviteAccepted'));
      navigate('/', { replace: true });
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('auth.inviteError')));
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <LoginLayout
      title=""
      portalTitle={t('auth.inviteAcceptTitle')}
      portalDescription={t('auth.inviteAcceptDescription')}
      portalDescriptionClassName="invite-accept-portal-description"
    >
      {!hasInviteToken ? (
        <div className="stack">
          <p className="section-copy">{t('auth.inviteMissingTokenBody')}</p>
          <p className="section-copy">{t('auth.inviteLinkHelp')}</p>
        </div>
      ) : (
        <form className="stack auth-form invite-accept-form" onSubmit={onSubmit}>
          <div className="form-grid form-grid--dense invite-accept-form__names">
            <InputForm
              className="invite-first-name field--onboarding"
              placeholder={'First Name'}
              autoComplete="given-name"
              variant="onboarding"
              leftIcon={<UserIcon className="onboarding-icon" />}
              error={errors.first_name?.message}
              {...register('first_name', {
                required: t('auth.firstNameRequired'),
              })}
            />
            <InputForm
              className="invite-last-name field--onboarding"
              placeholder={'Last Name'}
              autoComplete="family-name"
              variant="onboarding"
              leftIcon={<UserIcon className="onboarding-icon" />}
              error={errors.last_name?.message}
              {...register('last_name', {
                required: t('auth.lastNameRequired'),
              })}
            />
          </div>

          <InputForm
            className="invite-password field--onboarding"
            type="password"
            placeholder={"Set password"}
            autoComplete="new-password"
            variant="onboarding"
            leftIcon={<PasswordIcon className="onboarding-icon" />}
            rightIcon={<EyeIcon className="onboarding-icon" />}
            title={passwordValue ? t('auth.passwordRulesHint') : undefined}
            error={errors.password?.message}
            {...register('password', {
              required: t('auth.passwordRequired'),
              minLength: {
                value: 8,
                message: t('auth.passwordMinLength'),
              },
            })}
          />

          <InputForm
            className="invite-confirm-password field--onboarding"
            type="password"
            placeholder={"Confirm password"}
            autoComplete="new-password"
            variant="onboarding"
            leftIcon={<PasswordIcon className="onboarding-icon" />}
            rightIcon={<EyeIcon className="onboarding-icon" />}
            title={confirmPasswordValue ? t('auth.passwordRulesHint') : undefined}
            error={errors.password2?.message}
            {...register('password2', {
              required: t('auth.confirmPasswordRequired'),
              validate: (value, formValues) =>
                value === formValues.password || t('auth.passwordsMustMatch'),
            })}
          />

          <Button className="invite-submit invite-accept-submit button--block" variant="onboarding" type="submit" disabled={isSubmitting || !isValid}>
            {isSubmitting ? t('auth.acceptingInvite') : t('auth.completeInvite')}
          </Button>
        </form>
      )}
    </LoginLayout>
  );
}